import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface Estado {
  id: string;
  nome: string;
  sigla: string;
}

export interface Cidade {
  id: string;
  nome: string;
}

// Cache to avoid re-fetching
let estadosCache: Estado[] | null = null;
const cidadesCache: Record<string, Cidade[]> = {};

export function useEstados() {
  const [estados, setEstados] = useState<Estado[]>(estadosCache || []);

  useEffect(() => {
    if (estadosCache) return;

    supabase
      .from("estados")
      .select("id, nome, sigla")
      .order("nome")
      .then(({ data }) => {
        if (data) {
          estadosCache = data;
          setEstados(data);
        }
      });
  }, []);

  return estados;
}

export function useCidades(estadoNome: string) {
  const [cidades, setCidades] = useState<Cidade[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!estadoNome) {
      setCidades([]);
      return;
    }

    // Check cache
    if (cidadesCache[estadoNome] && cidadesCache[estadoNome].length > 0) {
      setCidades(cidadesCache[estadoNome]);
      return;
    }

    let cancelled = false;
    setLoading(true);

    const fetchCidades = async () => {
      try {
        const { data: estado } = await supabase
          .from("estados")
          .select("id")
          .eq("nome", estadoNome)
          .single();

        if (!estado || cancelled) {
          if (!cancelled) { setCidades([]); setLoading(false); }
          return;
        }

        // Fetch all cities (handle states with 1000+ cities)
        const allCidades: Cidade[] = [];
        let from = 0;
        const pageSize = 1000;

        while (true) {
          const { data } = await supabase
            .from("cidades")
            .select("id, nome")
            .eq("estado_id", estado.id)
            .order("nome")
            .range(from, from + pageSize - 1);

          if (cancelled) return;
          if (!data || data.length === 0) break;
          allCidades.push(...data);
          if (data.length < pageSize) break;
          from += pageSize;
        }

        if (!cancelled) {
          cidadesCache[estadoNome] = allCidades;
          setCidades(allCidades);
          setLoading(false);
        }
      } catch {
        if (!cancelled) { setCidades([]); setLoading(false); }
      }
    };

    fetchCidades();
    return () => { cancelled = true; };
  }, [estadoNome]);

  return { cidades, loading };
}

export function getEstadoNomes(): string[] {
  if (estadosCache) return estadosCache.map(e => e.nome);
  // Fallback static list for sync usage
  return [
    "Acre", "Alagoas", "Amapá", "Amazonas", "Bahia", "Ceará",
    "Distrito Federal", "Espírito Santo", "Goiás", "Maranhão",
    "Mato Grosso", "Mato Grosso do Sul", "Minas Gerais", "Pará",
    "Paraíba", "Paraná", "Pernambuco", "Piauí", "Rio de Janeiro",
    "Rio Grande do Norte", "Rio Grande do Sul", "Rondônia", "Roraima",
    "Santa Catarina", "São Paulo", "Sergipe", "Tocantins"
  ];
}
